import { Select, SelectOption } from '@acp/core-ui';
import React, { useEffect, useMemo } from 'react';
import { OnChangeValue, Props } from 'chakra-react-select';
import { usePropertyManagers } from '../hooks/use-property-managers';

type PropertyManagerOption = SelectOption<string>;

function PropertyManagerSelect<IsMulti extends boolean = false>({
    noPropertyManagerOption,
    isMulti = false as IsMulti,
    value,
    onChange,
    size,
    isClearable = true,
    isLabelDropdown = false,
    isSearchable,
    placeholderColor,
    placeholder = 'Select property manager',
    hidePlaceholderWhileLoading = false,
    stripUnknown = false,
    isDisabled = false,
}: {
    noPropertyManagerOption?: PropertyManagerOption;
    onChange: Props<PropertyManagerOption, IsMulti>['onChange'];
    value: IsMulti extends true ? string[] : string | null;
    isMulti?: IsMulti;
    size?: React.ComponentProps<typeof Select>['size'];
    isClearable?: boolean;
    isLabelDropdown?: boolean;
    isSearchable?: boolean;
    placeholderColor?: string;
    placeholder?: string;
    hidePlaceholderWhileLoading?: boolean;
    isDisabled?: boolean;

    /**
     * Automatically clear the value if no matching property manager is found
     * Only single selects are supported
     */
    stripUnknown?: boolean;
}) {
    const propertyManagersQuery = usePropertyManagers();

    const options = useMemo(() => {
        if (propertyManagersQuery.isLoading) {
            return [];
        }

        const propertyManagers = propertyManagersQuery.data ?? [];

        if (noPropertyManagerOption !== undefined) {
            return [
                ...propertyManagers.map((propertyManager) => ({
                    label: propertyManager.name,
                    value: propertyManager.id,
                })),
                noPropertyManagerOption,
            ];
        }

        return propertyManagers.map((propertyManager) => ({
            label: propertyManager.name,
            value: propertyManager.id,
        }));
    }, [propertyManagersQuery.data, propertyManagersQuery.isLoading, noPropertyManagerOption]);

    const selectedOption = useMemo(
        () => {
            if (isMulti) {
                return options.filter((option) => {
                    if (Array.isArray(value)) {
                        return value.includes(option.value);
                    }

                    return option.value === value;
                });
            }

            return options.find((option) => option.value === value);
        },
        [options, value, isMulti]
    );

    // If the value is non-null and no existing property manager is found,
    // stripUnknown will clear the value
    useEffect(() => {
        if (!stripUnknown || isMulti) return;

        if (propertyManagersQuery.isLoading) return;

        if (value === null) return;

        if (selectedOption !== undefined) return;

        if (onChange !== undefined) {
            onChange(
                null as OnChangeValue<PropertyManagerOption, IsMulti>,
                { action: 'clear', removedValues: [] }
            );
        }
    }, [
        isMulti,
        stripUnknown,
        selectedOption,
        value,
        options,
        propertyManagersQuery.isLoading,
    ]);

    return (
        <Select<PropertyManagerOption, IsMulti>
            isClearable={isClearable}
            options={options}
            onChange={onChange}
            value={selectedOption}
            isLoading={propertyManagersQuery.isLoading}
            placeholder={(hidePlaceholderWhileLoading && propertyManagersQuery.isLoading) ? '' : placeholder}
            isMulti={isMulti}
            size={size}
            isLabelDropdown={isLabelDropdown}
            isSearchable={isSearchable}
            placeholderColor={placeholderColor}
            isDisabled={isDisabled}
        />
    );
}

export { PropertyManagerOption, PropertyManagerSelect };
